import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { dateLocale } from '@/lib/locale';
import { useMerchantId } from '@/hooks/useMerchantId';
import { usePagination } from '@/hooks/use-pagination';
import { PaginationControls } from '@/components/PaginationControls';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Shield, ChevronRight, User, Calendar, Info } from 'lucide-react';

const actionLabel = (action: string): string =>
  i18n.exists(`misc_audit.a_${action}`) ? i18n.t(`misc_audit.a_${action}`) : action;

const ENTITY_COLORS: Record<string, string> = {
  sale: 'bg-success/10 text-success',
  product: 'bg-primary/10 text-primary',
  stock: 'bg-warning/10 text-warning',
  customer: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  user: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  branch: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  device: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
};

function formatDetailValue(key: string, value: any): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? i18n.t('misc_common.yes') : i18n.t('misc_common.no');
  if (key.includes('role')) {
    const roleLabels: Record<string, string> = { owner: i18n.t('misc_audit.role_owner'), manager: i18n.t('misc_audit.role_manager'), cashier: i18n.t('misc_audit.role_cashier'), viewer: i18n.t('misc_audit.role_viewer') };
    return roleLabels[value] || value;
  }
  if (key.includes('status')) {
    const statusLabels: Record<string, string> = { active: i18n.t('misc_audit.st_active'), blocked: i18n.t('misc_audit.st_blocked'), pending: i18n.t('misc_audit.st_pending'), lead: 'Lead' };
    return statusLabels[value] || value;
  }
  return String(value);
}

const detailLabel = (key: string): string =>
  i18n.exists(`misc_audit.d_${key}`) ? i18n.t(`misc_audit.d_${key}`) : key;

export default function AuditLogs() {
  const { t } = useTranslation();
  const { merchantId } = useMerchantId();
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState<any>(null);

  const { data: logsData, isLoading } = useQuery({
    queryKey: ['/audit-logs'],
    enabled: !!merchantId,
  });
  const logs = logsData?.data ?? [];

  const getUserName = (l: any) => {
    if (!l.user) return l.user_id ? String(l.user_id).slice(-7) : '—';
    return l.user.name || l.user.phone || String(l.user_id).slice(-7);
  };

  const filtered = logs.filter((l: any) => {
    const matchEntity = entityFilter === 'all' || l.entity_type === entityFilter;
    if (!matchEntity) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      actionLabel(l.action).toLowerCase().includes(s) ||
      l.entity_type?.toLowerCase().includes(s) ||
      l.entity_id?.toLowerCase().includes(s) ||
      getUserName(l).toLowerCase().includes(s)
    );
  });

  const entityTypes = [...new Set(logs.map((l: any) => l.entity_type))].sort();
  const pagination = usePagination(filtered, 25);

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in">
      <div className="flex items-center gap-2 mb-4">
        <Shield size={20} className="text-primary" />
        <h1 className="text-xl font-semibold text-foreground">Audit Log</h1>
        <Badge variant="secondary" className="ml-2 text-xs">{t('misc_audit.entries', { count: logs.length })}</Badge>
      </div>

      <div className="flex gap-3 items-center flex-wrap mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder={t('misc_audit.search_ph')} value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder={t('misc_audit.entity_type_ph')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('misc_audit.all')}</SelectItem>
            {entityTypes.map((et: string) => (
              <SelectItem key={et} value={et}>{et}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_audit.th_date')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_audit.th_who')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_audit.th_action')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_audit.th_entity')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_audit.th_detail')}</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t('misc_common.loading')}</td></tr>
            ) : pagination.items.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t('misc_audit.empty')}</td></tr>
            ) : (
              (pagination.items as any[]).map((l: any) => {
                const details = l.details || {};
                const summaryParts: string[] = [];
                if (details.new_role) summaryParts.push(t('misc_audit.sum_role', { value: formatDetailValue('role', details.new_role) }));
                if (details.new_status) summaryParts.push(t('misc_audit.sum_status', { value: formatDetailValue('status', details.new_status) }));
                if (details.user_name) summaryParts.push(details.user_name);
                if (details.email) summaryParts.push(details.email);
                if (details.total_amount) summaryParts.push(`${Number(details.total_amount).toLocaleString()} ${t('misc_common.currency')}`);
                const summary = summaryParts.join(' · ') || '—';

                return (
                  <tr
                    key={l.id}
                    className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => setSelectedLog(l)}
                  >
                    <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(l.created_at).toLocaleString(dateLocale(), { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        <User size={12} className="text-muted-foreground shrink-0" />
                        <span className="text-sm text-foreground font-medium truncate max-w-[120px]">{getUserName(l)}</span>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-foreground">
                      {actionLabel(l.action)}
                    </td>
                    <td className="p-3">
                      <Badge className={`text-xs border-0 ${ENTITY_COLORS[l.entity_type] || 'bg-muted text-muted-foreground'}`}>
                        {l.entity_type}
                      </Badge>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground max-w-[250px] truncate">
                      {summary}
                    </td>
                    <td className="p-3">
                      <ChevronRight size={14} className="text-muted-foreground" />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <PaginationControls
          page={pagination.page} totalPages={pagination.totalPages} total={pagination.total}
          pageSize={pagination.pageSize} onPrev={pagination.prevPage} onNext={pagination.nextPage}
          hasPrev={pagination.hasPrev} hasNext={pagination.hasNext}
        />
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info size={16} />
              {t('misc_audit.detail_title')}
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Calendar size={10} /> {t('misc_audit.th_date')}</p>
                  <p className="text-sm font-medium text-foreground">{new Date(selectedLog.created_at).toLocaleString(dateLocale())}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><User size={10} /> {t('misc_audit.performed_by')}</p>
                  <p className="text-sm font-medium text-foreground">{getUserName(selectedLog)}</p>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-muted/50 space-y-1">
                <p className="text-xs text-muted-foreground">{t('misc_audit.th_action')}</p>
                <p className="text-sm font-semibold text-foreground">{actionLabel(selectedLog.action)}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{t('misc_audit.entity_type_label')}</p>
                  <Badge className={`text-xs border-0 ${ENTITY_COLORS[selectedLog.entity_type] || 'bg-muted text-muted-foreground'}`}>
                    {selectedLog.entity_type}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{t('misc_audit.entity_id')}</p>
                  <p className="text-xs font-mono text-foreground">{selectedLog.entity_id || '—'}</p>
                </div>
              </div>

              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('misc_audit.changes')}</p>
                  <div className="rounded-lg border border-border divide-y divide-border">
                    {Object.entries(selectedLog.details).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center px-3 py-2">
                        <span className="text-xs text-muted-foreground">{detailLabel(key)}</span>
                        <span className="text-sm font-medium text-foreground">{formatDetailValue(key, value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
